import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useMountedState } from '../utils';
import { AdviceCard } from './AdviceCard';
import { Advice } from './types';

const ADVICE_TRANSITION_DELAY = 150;

interface AdviceContextType {
  closeAdvice: (cleanup?: boolean) => void;
  openAdvice: (advice: Advice) => void;
}

const AdviceContext = createContext<AdviceContextType | undefined>(undefined);

export const AdviceProvider: FC = ({ children }) => {
  const [isActiveAdvice, setIsActiveAdvice] = useState<boolean>(false);
  const [advice, setAdvice] = useState<Advice | null>(null);
  const isMounted = useMountedState();

  const closeAdvice = useCallback(
    (cleanup?: boolean) => {
      setIsActiveAdvice(false);

      if (cleanup && isMounted()) {
        setAdvice(null);
      }
    },
    [isMounted],
  );

  const openAdvice = useCallback(
    (candidateAdvice: Advice) => {
      closeAdvice();
      setAdvice(candidateAdvice);

      setTimeout(() => {
        setIsActiveAdvice(true);
      }, ADVICE_TRANSITION_DELAY);
    },
    [closeAdvice],
  );

  const {
    anchorRef,
    content,
    hasClickAwayListener,
    hasCross,
    tipPosition,
    PopperProps,
    zIndex,
  } = advice || {};

  return (
    <AdviceContext.Provider value={{ closeAdvice, openAdvice }}>
      {children}

      <AdviceCard
        anchorEl={anchorRef?.current}
        content={content}
        hasClickAwayListener={hasClickAwayListener}
        hasCross={hasCross}
        onCloseClick={closeAdvice}
        open={isActiveAdvice}
        tipPosition={tipPosition}
        zIndex={zIndex}
        {...PopperProps}
      />
    </AdviceContext.Provider>
  );
};

export const useAdvice = (): Pick<
  AdviceContextType,
  'openAdvice' | 'closeAdvice'
> => {
  const context = useContext(AdviceContext);

  if (!context) {
    throw new Error('You need to wrap useMenu inside AdviceProvider');
  }

  const { closeAdvice, ...rest } = context;

  useEffect(
    () => () => {
      closeAdvice(true);
    },
    [closeAdvice],
  );

  return { closeAdvice, ...rest };
};
